import { initializeApp } from 'firebase/app';
import { getFirestore, collection, addDoc, updateDoc, deleteDoc, doc, getDocs, getDoc, setDoc, query, where, orderBy } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { generateSlug, getUniqueSlug } from './slugUtils';

// Safely access environment variables
const getEnvVariable = (key) => {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env[key];
  }
  return undefined;
};

// Initialize Firebase in all environments
const firebaseConfig = {
  apiKey: getEnvVariable('VITE_FIREBASE_API_KEY'),
  authDomain: getEnvVariable('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: getEnvVariable('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: getEnvVariable('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: getEnvVariable('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: getEnvVariable('VITE_FIREBASE_APP_ID')
};

// Only initialize Firebase if we have the required config
let app, db, storage;

if (firebaseConfig.apiKey && firebaseConfig.projectId) {
  console.log('Initializing Firebase...');
  app = initializeApp(firebaseConfig);
  db = getFirestore(app);
  storage = getStorage(app);
  console.log('Firebase initialized successfully');
} else {
  console.warn('Firebase configuration is incomplete. Firebase services will not be available.');
}

// Helper to check if Firebase is initialized
const isFirebaseInitialized = () => !!app;

// Real implementations with fallbacks
export const getAllPosts = async () => {
  try {
    if (!isFirebaseInitialized()) return [];
    
    const postsQuery = query(collection(db, 'posts'), orderBy('publishedAt', 'desc'));
    const postsSnapshot = await getDocs(postsQuery);
    const posts = postsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    console.log('Retrieved posts from Firestore:', posts);
    return posts;
  } catch (error) {
    console.error('Error in getAllPosts:', error);
    return [];
  }
};

export const getPostBySlug = async (slug) => {
  if (!isFirebaseInitialized()) return null;
  
  const postsRef = collection(db, 'posts');
  const q = query(postsRef, where('slug', '==', slug));
  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) return null;
  const doc = querySnapshot.docs[0];
  return { id: doc.id, ...doc.data() };
};

export const savePost = async (post) => {
  try {
    console.log('savePost called with:', post);
    console.log('beforeImages:', post.beforeImages);
    console.log('afterImages:', post.afterImages);
    
    if (post.id) {
      console.log('Checking if post exists with ID:', post.id);
      const postRef = doc(db, 'posts', post.id);
      const docSnap = await getDoc(postRef);
      
      if (docSnap.exists()) {
        console.log('Updating existing post with ID:', post.id);
        // Create a copy without the ID to avoid Firestore errors
        const { id, ...postData } = post;
        await updateDoc(postRef, {
          ...postData,
          relatedServices: postData.relatedServices || []
        });
        return post;
      } else {
        console.log('Document does not exist, creating new post instead');
        // Remove ID and create new document
        const { id, ...postData } = post;
        const docRef = await addDoc(collection(db, 'posts'), {
          ...postData,
          relatedServices: postData.relatedServices || []
        });
        console.log('New post created with ID:', docRef.id);
        return { id: docRef.id, ...postData };
      }
    } else {
      console.log('Creating new post');
      // Remove any null ID field
      const { id, ...postData } = post;
      const docRef = await addDoc(collection(db, 'posts'), {
        ...postData,
        relatedServices: postData.relatedServices || []
      });
      console.log('New post created with ID:', docRef.id);
      return { id: docRef.id, ...post };
    }
  } catch (error) {
    console.error('Error saving post:', error);
    throw error;
  }
};

export const deletePost = async (id) => {
  try {
    console.log('Deleting post with ID:', id);
    if (!id) {
      throw new Error('No post ID provided for deletion');
    }
    const postRef = doc(db, 'posts', id);
    await deleteDoc(postRef);
    console.log('Post successfully deleted');
    return true;
  } catch (error) {
    console.error('Error deleting post:', error);
    throw error;
  }
};

export const uploadImage = async (file) => {
  if (!isFirebaseInitialized()) return null;
  
  const storageRef = ref(storage, `images/${file.name}`);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
};

export const uploadImages = async (files, category) => {
  if (!isFirebaseInitialized()) return [];
  
  // Convert FileList to Array if needed
  const filesArray = Array.isArray(files) ? files : Array.from(files);
  
  if (filesArray.length === 0) {
    console.warn('No files to upload');
    return [];
  }
  
  // Upload each file and create an object with category, name, and url
  const uploadPromises = filesArray.map(async (file) => {
    const timestamp = Date.now();
    const fileName = `${file.name}-${timestamp}`;
    const path = category ? `blog-images/${category}/${fileName}` : `images/${fileName}`;
    const storageRef = ref(storage, path);
    
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    
    // Return an object with category, name, and url
    return {
      category: category || 'general',
      name: file.name,
      url: url
    };
  });
  
  return Promise.all(uploadPromises);
};

export const verifyPostsExist = async (posts) => {
  if (!isFirebaseInitialized()) return [];
  
  const results = [];
  for (const post of posts) {
    const postRef = doc(db, 'posts', post.id);
    const docSnap = await getDoc(postRef);
    results.push({
      id: post.id,
      exists: docSnap.exists(),
      title: post.title
    });
  }
  return results;
};

export { db, storage }; 